import React, { useEffect, useRef } from 'react';
import { Route, Zap, Home, Layers, CheckCircle2, ArrowRight, Shield } from 'lucide-react';

const useScrollAnimation = (threshold = 0.08) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll('.fade-up, .fade-left, .fade-right, .scale-in').forEach((child) => {
            child.classList.add('visible');
          });
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
};

// ── Work type categories ────────────────────────────────────────────────────
const workTypes = [
  { icon: Route,  label: 'Pavimentación',    detail: 'Urbana y rural' },
  { icon: Zap,    label: 'Electrificación',  detail: 'Redes y alumbrado' },
  { icon: Home,   label: 'Vivienda social',  detail: 'Programas estatales' },
  { icon: Layers, label: 'Infraestructura',  detail: 'Obra civil y vial' },
];

// ── Government agencies ─────────────────────────────────────────────────────
const agencies = [
  {
    acronym: 'IOCIFED',
    fullName: 'Instituto Oaxaqueño Constructor de Infraestructura Educativa',
    level: 'Gobierno del Estado de Oaxaca',
    workType: 'Infraestructura educativa',
    typeColor: '#7C3AED',
    scope: [
      'Construcción y rehabilitación de escuelas',
      'Infraestructura educativa estatal',
      'Aulas, laboratorios y espacios educativos',
    ],
    description:
      'Ejecución de proyectos de infraestructura educativa para el estado de Oaxaca, garantizando espacios dignos y funcionales para la comunidad escolar.',
  },
  {
    acronym: 'SINFRA',
    fullName: 'Secretaría de Infraestructura del Estado de Oaxaca',
    level: 'Gobierno del Estado de Oaxaca',
    workType: 'Infraestructura y pavimentación',
    typeColor: '#1565C0',
    scope: [
      'Pavimentación de calles y vialidades',
      'Obras de urbanización y drenaje',
      'Proyectos de infraestructura vial',
    ],
    description:
      'Proyectos de pavimentación y obras de infraestructura vial para municipios del estado de Oaxaca, con apego a especificaciones técnicas oficiales.',
  },
  {
    acronym: 'SIC',
    fullName: 'Secretaría de Infraestructura y Comunicaciones',
    level: 'Gobierno del Estado de Oaxaca',
    workType: 'Comunicaciones y vialidades',
    typeColor: '#1B5E20',
    scope: [
      'Conservación y mantenimiento de vialidades',
      'Modernización carretera',
      'Revestimiento y trabajos de remanimiento',
    ],
    description:
      'Conservación, modernización y mantenimiento de vías de comunicación en el estado de Oaxaca, bajo los estándares técnicos de la dependencia.',
  },
  {
    acronym: 'VIVIENDA\nBIENESTAR',
    fullName: 'Programa de Vivienda Bienestar',
    level: 'Gobierno Federal',
    workType: 'Vivienda de interés social',
    typeColor: '#C41E3A',
    scope: [
      'Vivienda digna para familias vulnerables',
      'Ampliaciones y mejoramientos de vivienda',
      'Construcción de cuartos adicionales',
    ],
    description:
      'Ejecución de programas federales de vivienda social orientados a familias de escasos recursos, cumpliendo lineamientos y metas establecidas por la federación.',
  },
];

// ── Component ────────────────────────────────────────────────────────────────
const PublicWork = () => {
  const ref = useScrollAnimation();

  return (
    <section
      ref={ref}
      style={{
        padding: '8rem 2rem',
        background: 'var(--bg-primary)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Left institutional accent bar */}
      <div style={{
        position: 'absolute', left: 0, top: '10%', bottom: '10%',
        width: 4, background: 'var(--red)', borderRadius: '0 2px 2px 0',
      }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1200, margin: '0 auto' }}>

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div className="fade-up" style={{ marginBottom: '3.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
            <div className="section-label" style={{ margin: 0 }}>Obra pública</div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              background: 'rgba(196,30,58,0.08)', border: '1px solid rgba(196,30,58,0.2)',
              borderRadius: 20, padding: '0.2rem 0.75rem',
            }}>
              <Shield size={10} color="var(--red)" strokeWidth={2.5} />
              <span style={{
                fontFamily: 'var(--font-heading)', fontWeight: 700,
                fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase',
                color: 'var(--red)',
              }}>
                Estatal y Federal
              </span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '3rem', alignItems: 'flex-end' }} className="pw-header-grid">
            <div>
              <h2 style={{
                fontFamily: 'var(--font-heading)', fontWeight: 900,
                fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'var(--text-primary)',
                lineHeight: 1.15, marginBottom: '1rem',
              }}>
                Experiencia en{' '}
                <span style={{ color: 'var(--red)' }}>obra pública</span>
                <br />estatal y municipal
              </h2>
              <p style={{
                fontFamily: 'var(--font-body)', fontSize: '1.05rem',
                color: 'var(--gray-dark)', lineHeight: 1.8, maxWidth: 620,
              }}>
                Contamos con experiencia en ejecución de obra pública a nivel estatal y municipal,
                trabajando directamente con dependencias de gobierno en proyectos de pavimentación,
                electrificación, vivienda e infraestructura en todo el estado de Oaxaca.
              </p>
            </div>

            {/* Side stat */}
            <div style={{
              textAlign: 'center', flexShrink: 0,
              padding: '1.5rem 2rem',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: 16,
            }} className="pw-side-stat">
              <div style={{
                fontFamily: 'var(--font-heading)', fontWeight: 900,
                fontSize: '2.5rem', color: 'var(--text-primary)', lineHeight: 1,
              }}>
                4<span style={{ color: 'var(--red)' }}>+</span>
              </div>
              <div style={{
                fontFamily: 'var(--font-body)', fontSize: '0.78rem',
                color: 'var(--gray)', marginTop: '0.4rem', lineHeight: 1.4,
              }}>
                dependencias<br />gubernamentales
              </div>
            </div>
          </div>
        </div>

        {/* ── WORK TYPE CHIPS ────────────────────────────────────────────── */}
        <div className="fade-up" style={{
          display: 'flex', gap: '1rem', flexWrap: 'wrap',
          marginBottom: '3.5rem',
        }}>
          {workTypes.map((wt, i) => {
            const Icon = wt.icon;
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: 10, padding: '0.7rem 1.25rem',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'var(--red)', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={16} color="white" strokeWidth={1.75} />
                </div>
                <div>
                  <div style={{
                    fontFamily: 'var(--font-heading)', fontWeight: 700,
                    fontSize: '0.82rem', color: 'var(--text-primary)',
                  }}>{wt.label}</div>
                  <div style={{
                    fontFamily: 'var(--font-body)', fontSize: '0.68rem',
                    color: 'var(--gray)',
                  }}>{wt.detail}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── AGENCY CARDS ───────────────────────────────────────────────── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '1.5rem',
          marginBottom: '3.5rem',
        }} className="pw-grid">
          {agencies.map((ag, i) => (
            <div
              key={i}
              className={`fade-up delay-${i + 1}`}
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderLeft: `4px solid ${ag.typeColor}`,
                borderRadius: 16,
                padding: '2rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                cursor: 'default',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Card header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                <div>
                  {/* Acronym */}
                  <div style={{
                    fontFamily: 'var(--font-heading)', fontWeight: 900,
                    fontSize: 'clamp(1.6rem, 3vw, 2.1rem)',
                    color: 'var(--text-primary)', lineHeight: 1, letterSpacing: '-0.02em',
                    whiteSpace: 'pre-line',
                  }}>
                    {ag.acronym}
                  </div>
                  {/* Full name */}
                  <div style={{
                    fontFamily: 'var(--font-body)', fontSize: '0.75rem',
                    color: 'var(--gray)', marginTop: '0.4rem', lineHeight: 1.4,
                  }}>
                    {ag.fullName}
                  </div>
                </div>

                {/* Level badge */}
                <span style={{
                  display: 'inline-block', flexShrink: 0,
                  background: ag.typeColor + '12',
                  border: `1px solid ${ag.typeColor}30`,
                  color: ag.typeColor,
                  fontFamily: 'var(--font-heading)', fontWeight: 700,
                  fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase',
                  padding: '0.3rem 0.65rem', borderRadius: 4,
                  marginTop: 4,
                }}>
                  {ag.level}
                </span>
              </div>

              {/* Divider */}
              <div style={{ height: 1, background: 'var(--border-color)' }} />

              {/* Work type label */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: ag.typeColor, flexShrink: 0 }} />
                <span style={{
                  fontFamily: 'var(--font-heading)', fontWeight: 700,
                  fontSize: '0.72rem', color: 'var(--gray-dark)', letterSpacing: '0.04em',
                }}>
                  {ag.workType}
                </span>
              </div>

              {/* Description */}
              <p style={{
                fontFamily: 'var(--font-body)', fontSize: '0.83rem',
                color: 'var(--gray-dark)', lineHeight: 1.75,
              }}>
                {ag.description}
              </p>

              {/* Scope list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {ag.scope.map((item, j) => (
                  <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <CheckCircle2 size={13} color={ag.typeColor} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span style={{
                      fontFamily: 'var(--font-body)', fontSize: '0.78rem',
                      color: 'var(--gray)', lineHeight: 1.5,
                    }}>
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ── TRUST STATEMENT ────────────────────────────────────────────── */}
        <div className="fade-up" style={{
          background: 'var(--band-bg)',
          boxShadow: 'inset 0 0 0 1px var(--band-ring)',
          borderRadius: 16,
          padding: '2.25rem 2.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '2rem',
          flexWrap: 'wrap',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* glow */}
          <div style={{
            position: 'absolute', right: 0, top: 0, bottom: 0, width: '40%',
            background: 'radial-gradient(ellipse at right, rgba(196,30,58,0.12) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              fontFamily: 'var(--font-heading)', fontWeight: 900,
              fontSize: 'clamp(1rem, 2vw, 1.25rem)', color: 'var(--band-text)',
              lineHeight: 1.3, marginBottom: '0.5rem',
            }}>
              Empresa registrada y habilitada para licitaciones de obra pública
            </div>
            <p style={{
              fontFamily: 'var(--font-body)', fontSize: '0.875rem',
              color: 'var(--band-text-55)', lineHeight: 1.6,
            }}>
              Participamos en procesos de licitación a nivel estatal y federal, con expediente
              técnico, capacidad financiera y experiencia comprobada en cada disciplina.
            </p>
          </div>

          <button
            onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0,
              background: 'var(--red)', color: 'white',
              fontFamily: 'var(--font-heading)', fontWeight: 700,
              fontSize: '0.82rem', letterSpacing: '0.06em', textTransform: 'uppercase',
              border: 'none', borderRadius: 8, padding: '0.875rem 1.75rem',
              cursor: 'pointer', whiteSpace: 'nowrap',
              boxShadow: '0 6px 24px rgba(196,30,58,0.35)',
              transition: 'background 0.3s ease, transform 0.2s ease',
              position: 'relative', zIndex: 1,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--red-dark)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--red)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Contáctanos <ArrowRight size={15} />
          </button>
        </div>

      </div>

      <style>{`
        @media (max-width: 860px) {
          .pw-grid { grid-template-columns: 1fr !important; }
          .pw-header-grid { grid-template-columns: 1fr !important; }
          .pw-side-stat { display: none !important; }
        }
        @media (max-width: 600px) {
          .pw-grid > div { border-left-width: 3px !important; }
        }
      `}</style>
    </section>
  );
};

export default PublicWork;
